import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

const DEFAULT_PER_PAGE = 15;

export const useAdminFailedOperations = ({
  queuePage = 1,
  outboxPage = 1,
  stripePage = 1,
  perPage = DEFAULT_PER_PAGE,
} = {}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['adminFailedOperations', queuePage, outboxPage, stripePage, perPage],
    queryFn: async () => {
      const response = await api.get('/admin/failed-operations', {
        params: {
          queue_page: queuePage,
          outbox_page: outboxPage,
          stripe_page: stripePage,
          per_page: perPage,
        },
      });
      return response.data;
    },
    staleTime: 30 * 1000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['adminFailedOperations'] });
    queryClient.invalidateQueries({ queryKey: ['adminInfrastructure'] });
  };

  const retryQueueJob = useMutation({
    mutationFn: async (uuid) => {
      const response = await api.post(`/admin/failed-operations/queue/${uuid}/retry`);
      return response.data;
    },
    onSuccess: invalidate,
  });

  const retryOutboxEvent = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/admin/failed-operations/outbox/${id}/retry`);
      return response.data;
    },
    onSuccess: invalidate,
  });

  const retryStripeWebhook = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/admin/failed-operations/stripe/${id}/retry`);
      return response.data;
    },
    onSuccess: invalidate,
  });

  const retryAllQueueJobs = useMutation({
    mutationFn: async (limit = 50) => {
      const response = await api.post('/admin/failed-operations/queue/retry-all', null, {
        params: { limit },
      });
      return response.data;
    },
    onSuccess: invalidate,
  });

  const retryAllOutboxEvents = useMutation({
    mutationFn: async (limit = 50) => {
      const response = await api.post('/admin/failed-operations/outbox/retry-all', null, {
        params: { limit },
      });
      return response.data;
    },
    onSuccess: invalidate,
  });

  const retryAllStripeWebhooks = useMutation({
    mutationFn: async (limit = 25) => {
      const response = await api.post('/admin/failed-operations/stripe/retry-all', null, {
        params: { limit },
      });
      return response.data;
    },
    onSuccess: invalidate,
  });

  const payload = data?.data || null;

  return {
    summary: payload?.summary || null,
    perPage: payload?.per_page || perPage,
    queueJobs: payload?.queue_jobs?.data || [],
    queueMeta: payload?.queue_jobs || null,
    outboxEvents: payload?.outbox_events?.data || [],
    outboxMeta: payload?.outbox_events || null,
    stripeWebhooks: payload?.stripe_webhooks?.data || [],
    stripeMeta: payload?.stripe_webhooks || null,
    isLoading,
    error,
    refetch,
    retryQueueJob,
    retryOutboxEvent,
    retryStripeWebhook,
    retryAllQueueJobs,
    retryAllOutboxEvents,
    retryAllStripeWebhooks,
    isRetrying:
      retryQueueJob.isPending ||
      retryOutboxEvent.isPending ||
      retryStripeWebhook.isPending ||
      retryAllQueueJobs.isPending ||
      retryAllOutboxEvents.isPending ||
      retryAllStripeWebhooks.isPending,
  };
};
